import type { EHRProvider } from '@heidi/schema/ml-scribe';
import { useQuery } from '@tanstack/react-query';

import { connectedAppsQueryKeys } from '../../contracts/queryKeys';
import type { IntegrationClientMode } from '../../contracts/types';
import { getIntegrationCapabilities } from '../api/marketplace';

/**
 * Reads what a provider's integration can do (push note, sync document, …).
 *
 * Carved from `src/hooks/query/integrations/general/` at ARCFE-1087 — headless.
 * The `enabled` gate and the redundant in-`queryFn` throw are both original:
 * `enabled` stops the fetch, and the throw narrows the type for TypeScript
 * without ever running.
 *
 * Pass `mode` only where the answer should depend on whether Heidi was opened
 * from an EHR launch; without it the backend ignores the client's mode.
 */
export function useQueryIntegrationCapabilities(
  provider: EHRProvider | string | null | undefined,
  mode?: IntegrationClientMode
) {
  return useQuery({
    queryKey: connectedAppsQueryKeys.integrationCapabilities(provider, mode),
    enabled: !!provider,
    queryFn: async () => {
      if (!provider) {
        throw new Error('Provider is not defined');
      }
      const { data } = await getIntegrationCapabilities(provider, mode);
      return data;
    },
  });
}
